import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

const stats = [
  { value: 20, suffix: '+', label: 'YEARS', desc: 'Since 2004. Every technology wave.' },
  { value: 5, suffix: ' in 1', label: 'PRACTICES, ONE PARTNER', desc: 'AI. Security. Apps. Cloud. Modernization.' },
  { value: 3, suffix: '', label: 'CONTINENTS', desc: 'US headquarters. Global delivery.' },
  { value: 100, suffix: '%', label: 'ACCOUNTABLE', desc: 'We measure outcomes, not hours.' },
];

function useCountUp(target, duration = 1500, trigger = false) {
  const [count, setCount] = useState(0);
  const hasRun = useRef(false);
  useEffect(() => {
  if (!trigger || hasRun.current) return;
  hasRun.current = true;
  const start = performance.now();
  function step(now) {
  const elapsed = now - start;
  const progress = Math.min(elapsed / duration, 1);
  const eased = 1 - Math.pow(1 - progress, 3);
  setCount(Math.round(eased * target));
  if (progress < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
  }, [trigger, target, duration]);
  return count;
}

function StatItem({ stat, inView }) {
  const count = useCountUp(stat.value, 1500, inView);
  return (
  <div className="text-center py-8 px-4">
  <div className="font-display font-black text-white mb-2" style={{ fontSize: 'clamp(2.5rem, 2rem + 2vw, 3.5rem)' }}>
  {stat.prefix || ''}{count}{stat.suffix || ''}
  </div>
  <div className="text-orange-500 text-sm font-semibold uppercase tracking-wider mb-2">{stat.label}</div>
  <p className="text-white/50 text-sm leading-relaxed max-w-[200px] mx-auto">{stat.desc}</p>
  </div>
  );
}

export default function FlynautEdge() {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
  const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.25 });
  if (sectionRef.current) obs.observe(sectionRef.current);
  return () => obs.disconnect();
  }, []);

  return (
  <section data-testid="flynaut-edge-section" className="bg-white py-16 lg:py-20">
  <div className="max-w-content mx-auto px-6">
  {/* Heading */}
  <motion.div
  initial={{ opacity: 0, y: 24 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.5 }}
  className="mb-14"
  >
  <p className="text-overline text-orange-500 mb-3">THE FLYNAUT EDGE</p>
  <h2 data-testid="flynaut-edge-heading" className="text-h2 text-navy-800 font-black tracking-tight">Why Organizations Choose Flynaut</h2>
  </motion.div>

  {/* Stats Block - dark with bg image overlay */}
  <div
  ref={sectionRef}
  className="relative rounded-2xl overflow-hidden mb-14"
  >
  {/* BG image */}
  <img
  src="https://images.unsplash.com/photo-1766740606233-6573571caa6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBlbnRlcnByaXNlJTIwb2ZmaWNlJTIwYnVpbGRpbmclMjBleHRlcmlvcnxlbnwwfHx8fDE3NzQ3MzEyNDh8MA&ixlib=rb-4.1.0&q=85"
  alt=""
  className="absolute inset-0 w-full h-full object-cover"
  loading="lazy"
  />
  {/* Heavy dark overlay */}
  <div className="absolute inset-0 bg-navy-900/90" />

  <div className="relative z-10 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
  {stats.map((stat, i) => (
  <motion.div
  key={stat.label}
  data-testid={`edge-stat-${i}`}
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.4, delay: i * 0.1 }}
  >
  <StatItem stat={stat} inView={inView} />
  </motion.div>
  ))}
  </div>
  </div>

  {/* Competitive Contrast Block */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16">
  <motion.div
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.5 }}
  data-testid="edge-enterprise-depth"
  className="md:border-r md:border-navy-200 md:pr-12"
  >
  <h3 className="text-navy-800 font-black tracking-tight mb-4" style={{ fontSize: 'clamp(1.5rem, 1.2rem + 1vw, 2.25rem)' }}>Built for this.</h3>
  <p className="text-navy-600 text-base lg:text-lg leading-relaxed">
  Five specialized practices. One integrated team. We do not hand you off between departments or bury you in process. Every engagement starts with a senior architect who stays through delivery.
  </p>
  </motion.div>

  <motion.div
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  viewport={{ once: true }}
  transition={{ duration: 0.5, delay: 0.1 }}
  data-testid="edge-no-overhead"
  >
  <h3 className="text-navy-800 font-black tracking-tight mb-4" style={{ fontSize: 'clamp(1.5rem, 1.2rem + 1vw, 2.25rem)' }}>Built to last.</h3>
  <p className="text-navy-600 text-base lg:text-lg leading-relaxed">
  We have been doing this since 2004 because we deliver and because clients stay. Not through contracts, through results. The kind of results that turn a first project into a five-year partnership.
  </p>
  </motion.div>
  </div>
  </div>
  </section>
  );
}
